import { join } from "node:path";

import { CliError } from "../errors/index.js";
import { PositionEvaluationSchema } from "../model/evaluation.js";
import {
  JsonObjectSchema,
  type JsonObject,
  PositionIdSchema,
} from "../model/common.js";
import { BoardStateSchema, PositionRecordSchema, type PositionRecord } from "../model/position.js";
import type { ExecutionContext } from "../runtime/context.js";
import { ensureDirectory } from "../runtime/filesystem.js";
import {
  type SqliteDatabase,
  type SqliteRow,
  openSqliteDatabase,
} from "../runtime/sqlite.js";
import { isWalSafe } from "../runtime/sqlite-version.js";
import { boardDirectory } from "./board-meta.js";

type CounterKind = "atom" | "cell" | "position";
type CounterPrefix = "a" | "c" | "p";

export async function boardStorePath(
  context: ExecutionContext,
  sessionKey: string,
) {
  const directory = boardDirectory(context, sessionKey);
  await ensureDirectory(directory);
  return join(directory, "store.db");
}

export async function withBoardDatabase<T>(
  context: ExecutionContext,
  sessionKey: string,
  run: (database: SqliteDatabase) => T,
) {
  const storePath = await boardStorePath(context, sessionKey);
  const database = await openSqliteDatabase(storePath);

  try {
    configureDatabase(database);
    migrateDatabase(database);
    return run(database);
  } finally {
    database.close();
  }
}

export function withWriteTransaction<T>(
  database: SqliteDatabase,
  run: () => T,
) {
  database.exec("begin immediate transaction");
  try {
    const result = run();
    database.exec("commit");
    return result;
  } catch (error) {
    database.exec("rollback");
    throw error;
  }
}

export function initializeBoardStore(
  database: SqliteDatabase,
  rootPosition: PositionRecord,
) {
  withWriteTransaction(database, () => {
    if (positionCount(database) > 0) {
      throw new CliError("BOARD_ALREADY_EXISTS", "Board store already contains positions");
    }

    seedCounter(database, "position", 0);
    seedCounter(database, "atom", 0);
    seedCounter(database, "cell", 0);
    insertPosition(database, rootPosition);
  });
}

export function allocateRecordId(
  database: SqliteDatabase,
  kind: CounterKind,
  prefix: CounterPrefix,
  requestedId: string | undefined,
) {
  if (requestedId !== undefined) {
    const safeId = PositionIdSchema.parse(requestedId);
    if (recordExists(database, safeId)) {
      throw new CliError("REF_ALREADY_EXISTS", `Ref "${safeId}" already exists`);
    }
    return safeId;
  }

  const current = readCounter(database, kind);
  const nextValue = current + 1;
  database.prepare(`update counters set value = ? where kind = ?`).run(nextValue, kind);
  return `${prefix}${nextValue}`;
}

export function insertPosition(database: SqliteDatabase, position: PositionRecord) {
  database
    .prepare(
      `insert into positions (
        id,
        parent_position_id,
        depth,
        move_kind,
        move_summary,
        move_payload,
        state_snapshot,
        state_hash,
        evaluation,
        created_at
      ) values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    )
    .run(
      position.id,
      position.parentPositionId ?? null,
      position.depth,
      position.moveKind,
      position.moveSummary,
      stringify(position.movePayload),
      JSON.stringify(position.stateSnapshot),
      position.stateHash,
      JSON.stringify(position.evaluation),
      position.createdAt,
    );
}

export function loadPosition(database: SqliteDatabase, positionId: string) {
  return mapPositionRow(
    database.prepare(`select * from positions where id = ? limit 1`).get(positionId),
  );
}

export function listPositions(database: SqliteDatabase) {
  return database
    .prepare(`select * from positions order by created_at asc`)
    .all()
    .map(mapPositionRow)
    .filter((row): row is PositionRecord => row !== undefined);
}

export function loadLatestArtifact(database: SqliteDatabase, kind: string) {
  const row = database
    .prepare(
      `select * from artifacts where kind = ? order by created_at desc limit 1`,
    )
    .get(kind);
  if (row === undefined) {
    return undefined;
  }
  return {
    content: asString(row, "content"),
    createdAt: asString(row, "created_at"),
    kind: asString(row, "kind"),
    relativePath: asString(row, "relative_path"),
  };
}

export function writeArtifact(
  database: SqliteDatabase,
  input: { content: string; kind: string; relativePath: string },
) {
  database
    .prepare(
      `insert into artifacts (id, kind, relative_path, content, created_at)
       values (lower(hex(randomblob(16))), ?, ?, ?, ?)`,
    )
    .run(input.kind, input.relativePath, input.content, new Date().toISOString());
}

export function positionCount(database: SqliteDatabase) {
  const row = database.prepare(`select count(*) as count from positions`).get();
  return asNumber(row, "count");
}

function configureDatabase(database: SqliteDatabase) {
  const version = database.sqliteVersion ?? "0.0.0";
  const journalMode = isWalSafe(version) ? "WAL" : "DELETE";
  database.exec(`pragma journal_mode = ${journalMode}`);
  database.exec("pragma synchronous = NORMAL");
  database.exec("pragma busy_timeout = 5000");
  database.exec("pragma foreign_keys = ON");
}

function migrateDatabase(database: SqliteDatabase) {
  database.exec(`
    create table if not exists counters (
      kind text primary key,
      value integer not null
    ) strict;

    create table if not exists positions (
      id text primary key,
      parent_position_id text,
      depth integer not null,
      move_kind text not null,
      move_summary text not null,
      move_payload text,
      state_snapshot text not null,
      state_hash text not null,
      evaluation text not null,
      created_at text not null
    ) strict;

    create index if not exists positions_parent_idx
      on positions(parent_position_id, created_at);

    create index if not exists positions_state_hash_idx
      on positions(state_hash);

    create table if not exists artifacts (
      id text primary key,
      kind text not null,
      relative_path text not null,
      content text not null,
      created_at text not null
    ) strict;
  `);
}

function seedCounter(
  database: SqliteDatabase,
  kind: CounterKind,
  value: number,
) {
  database.prepare(`insert or ignore into counters (kind, value) values (?, ?)`).run(kind, value);
}

function readCounter(database: SqliteDatabase, kind: CounterKind) {
  seedCounter(database, kind, 0);
  const row = database
    .prepare(`select value from counters where kind = ? limit 1`)
    .get(kind);
  return asNumber(row, "value");
}

function recordExists(database: SqliteDatabase, ref: string) {
  const row = database
    .prepare(`select 1 as present from positions where id = ? limit 1`)
    .get(ref);
  return row !== undefined;
}

function mapPositionRow(row: SqliteRow | undefined): PositionRecord | undefined {
  if (row === undefined) {
    return undefined;
  }

  return PositionRecordSchema.parse({
    createdAt: asString(row, "created_at"),
    depth: asNumber(row, "depth"),
    evaluation: parseJson(PositionEvaluationSchema, asString(row, "evaluation")),
    id: asString(row, "id"),
    moveKind: asString(row, "move_kind"),
    movePayload: parseJsonObject(asNullableString(row, "move_payload")) ?? undefined,
    moveSummary: asString(row, "move_summary"),
    parentPositionId: asNullableString(row, "parent_position_id") ?? undefined,
    stateHash: asString(row, "state_hash"),
    stateSnapshot: parseJson(BoardStateSchema, asString(row, "state_snapshot")),
  });
}

function stringify(value: JsonObject | undefined) {
  if (value === undefined) {
    return null;
  }
  return JSON.stringify(value);
}

function parseJsonObject(value: string | null) {
  if (value === null) {
    return undefined;
  }
  return parseJson(JsonObjectSchema, value);
}

function parseJson<T>(
  schema: { parse: (value: unknown) => T },
  value: string,
) {
  return schema.parse(JSON.parse(value));
}

function asNumber(row: SqliteRow | undefined, key: string) {
  const value = row?.[key];
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "bigint") {
    return Number(value);
  }
  throw new Error(`Expected numeric column "${key}"`);
}

function asString(row: SqliteRow | undefined, key: string) {
  const value = row?.[key];
  if (typeof value !== "string") {
    throw new Error(`Expected text column "${key}"`);
  }
  return value;
}

function asNullableString(row: SqliteRow | undefined, key: string) {
  const value = row?.[key];
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== "string") {
    throw new Error(`Expected nullable text column "${key}"`);
  }
  return value;
}
